/* tslint:disable:typedef-whitespace whitespace no-trailing-whitespace */

/** common helpers */

/**
 *  some web calls return errors in a variety of ways; this function will try to take any of them
 *  and just give us a text error we can show/use.
 *
 */
export function errorAsText(error: any) {

  let errMsg: string = error;

  if (typeof error !== 'string') {

    if (typeof error.error === 'string') {

      errMsg = error.error;
    }

    if (typeof error.message === 'string') {

      errMsg = error.message;
    }

    if (typeof error.error.message === 'string') {

      errMsg = error.error.message;
    }
  }

  if (typeof errMsg !== 'string') {

    console.log('errorAsText() can not decode: ', error);

    /* we can't decode this. */

    return 'Unknown error';
  }

  /* try to match GuzzleHTTP exceptions */

  const exceptionError: string[] = errMsg.match(/`(\S+)\s+([^`]+)`\s+resulted\s+in\s+a\s+`([^`]+)`/);

  if (Array.isArray(exceptionError)) {

    const verb = exceptionError[1];
    const url  = exceptionError[2];
    const msg  = exceptionError[3];

    errMsg = `[${verb}] ${msg} (${url})`;
  }

  /* try to match standard HTML error pages */

  const htmlError: string[] = errMsg.match(/<h2>(.*)<\/h2>/);

  if (Array.isArray(htmlError)) {

    errMsg = htmlError[1];

    errMsg = errMsg.replace(/<p>/g,   '\n');
    errMsg = errMsg.replace(/<\/p>/g, '');

  }

  return errMsg;
}

/**
 *
 * ago() - format the given JavaScript Date, return a Twitter/Facebook style time - ago
 *
 * @param date        - JavaScript Date (date/time object)
 * @param granularity - the resolution to use
 *
 * @return string - the formatted time
 *
 */
export function ago(date: any, granularity: number = 2): string {

  if (typeof date === 'string') {

    date = fromMySQLDate(date as string);

  } else {

    date = date as Date;
  }

  let       retval: string = '';
  let   difference: number = (Date.now() / 1000) - (date.valueOf() / 1000);

  const    periods: [string, number][] = [
    ['decade', 315360000],
    ['year',   31536000],
    ['month',  2628000],
    ['week',   604800],
    ['day',    86400],
    ['hour',   3600],
    ['minute', 60],
    ['second', 1]
  ];

  periods.forEach( (item: [string, number]) => {

    if (granularity === 0) {

      return ;
    }

    const   key: string = item[0];
    const value: number = item[1];

    if (difference >= value) {

      const time: number = Math.floor(difference / value);

      difference %= value;
      retval     += (retval ? ' ' : '') + time + ' ';
      retval     += ((time > 1) ? key + 's' : key);

      granularity--;
    }

  });

  if ( empty(retval)) {

    retval        = 'a moment';
  }

  return retval;
}

/** take the given MySQL/Unix style dateTime and convert to Java Date object */
export function fromMySQLDate(dateTime: string): Date {

  /* Split timestamp into [ Y, M, D, h, m, s ] */
  const t: string[] = dateTime.split(/[- :]/);

  /* render */

  return new Date(Date.UTC(
    parseInt(t[0], 10),
    parseInt(t[1], 10) - 1,
    parseInt(t[2], 10),
    parseInt(t[3], 10),
    parseInt(t[4], 10),
    parseInt(t[5], 10)
  ));
}

/** helper to extract the directory of the given path */
export function dirname(path): string {

  return path.replace(/\\/g, '/').replace(/\/[^/]*\/?$/, '');
}

/** helper to get the quick basename or file part of something that kind looks like a path */
export function basename(path): string {

  let base = path.substring(path.lastIndexOf('/') + 1);

  if (!base || base === '' || base === path) {

    base = path.substring(path.lastIndexOf('\\') + 1);

    if (!base || base === '' || base === path) {

      base = path;
    }
  }

  return base;
}

/**
 * helper to return a map of optional parameters in a URL
 *
 *   https://stackoverflow.com/questions/8486099/how-do-i-parse-a-url-query-parameters-in-javascript
 */
export function getUrlParams(url) {

  if (!url) {

    /* nothing to do */

    return {};
  }

  const question = url.indexOf('?');
  let       hash = url.indexOf('#');

  if ((hash === -1 && question === -1)) {

    /* nothing to do */

    return {};
  }

  if (hash === -1) {

    hash = url.length;
  }

  const query  = ((question === -1) || (hash === question+1)) ? url.substring(hash) : url.substring(question+1,hash);
  const result = {};

  query.split('&').forEach((part) => {

    if (!part) {

      return;
    }

    part       = part.split('+').join(' ');

    const eq   = part.indexOf('=');
    let   key  = (eq > -1) ? part.substr(0, eq) : part;
    const val  = (eq > -1) ? decodeURIComponent(part.substr(eq + 1)) : '';
    const from = key.indexOf('[');

    if (from === -1) {

      result[decodeURIComponent(key)] = val;

    } else {

      const    to = key.indexOf("]", from);
      const index = decodeURIComponent(key.substring(from + 1, to));
      key         = decodeURIComponent(key.substring(0, from));

      if (!result[key]) {

        result[key] = [];
      }

      if (!index) {

        result[key].push(val);

      } else {
        result[key][index] = val;
      }
    }
  });

  return result;
}

/** fetch human readable size of bytes */
export function bytesToSize(bytes, decimals = 2) {

  /* @link https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript */

  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/** @brief helper to test if a property or variable exists */
export function isset(mixedVar: any): boolean {

  if (typeof mixedVar !== 'undefined') {

    return true;
  }

  return false;
}

/** @brief test if the given value is an array. */
export function isarray(value) {
  return value && typeof value === 'object' && value.constructor === Array;
}

const charCodeZero:number = '0'.charCodeAt(0);
const charCodeNine:number = '9'.charCodeAt(0);

/** @brief check if something is empty */
export function empty(mixedVar: any) {

  if(typeof mixedVar === null) {

    return true;
  }

  if(typeof mixedVar === 'undefined') {

    return true;
  }

  if(typeof mixedVar === 'boolean') {

    return mixedVar;
  }

  if(typeof mixedVar.length !== 'undefined') {

    return mixedVar.length === 0;
  }

  return false;
}

/** @brief helper to test if a given character (char[0]) is a digit */
export function isdigit(text: string, pos: number) {

  /* @link https://stackoverflow.com/questions/8935632/check-if-character-is-number */

  const n:number = text.charCodeAt(pos);

  return ((n >= charCodeZero) && (n <= charCodeNine));
}

/** @brief helper to convert Days:H:M:S to ISO 8601 period string */
export function asISO8601(days: number, hours: number, minutes: number, seconds: number) {

  let formatted: string = `P${zeroPad(days, 3)}DT${zeroPad(hours)}H${zeroPad(minutes)}M${zeroPad(seconds)}S`;

  return formatted;
}

/** pad (left size) a value with 0's so it has the given size */
export function zeroPad(value: number, size: number = 2): string {

  let s = String(value);

  while (s.length < (size || 2)) {

    s = `0${s}`;
  }

  return s;
}

/**
 *
 * @brief helper to convert from seconds to ISO 8601 period string
 *
 * @link https://en.wikipedia.org/wiki/ISO_8601
 * @link https://www.digi.com/resources/documentation/digidocs/90001437-13/reference/r_iso_8601_duration_format.htm
 * @link ftp://ftp.ripe.net/test-traffic/ROOT/delayplots/iso8601.C
 *
 */
export function fromSecondsISO8601(seconds: number) {

  /* for the day part, we only support 3 digits; i.e. max of 999 days */

  if (seconds > 86313600) {

    seconds = 86313600; /* 999 days */
  }

  let days:    number = 0;
  let hours:   number = 0;
  let minutes: number = 0;
  let sec:     number = 0;

  if (seconds >= 60) {

    minutes += (seconds / 60);

    sec %= 60;
  }

  if (minutes >= 60) {

    hours += (minutes / 60);

    minutes %= 60;
  }

  if (hours >= 24) {

    days += (hours / 24);

    hours %= 24;
  }

  let formatted: string = `P${zeroPad(days, 3)}DT${zeroPad(hours)}H${zeroPad(minutes)}M${zeroPad(sec)}S`;

  return formatted;
}

/** move an item in place in array, and return the same array */
export function array_move(arr, old_index, new_index) {

  if (new_index >= arr.length) {

    let k: number = new_index - arr.length + 1;

    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);

  return arr;
}

/*
 * intersection of two lines:
 *
 *   ref: https://www.geeksforgeeks.org/check-if-two-given-line-segments-intersect/
 */

/** Given three colinear points p, q, r, the function checks if point q lies on line segment 'pr' */
function onSegment(px: number, py: number, qx: number, qy: number, rx: number, ry: number): boolean {

  if (qx <= Math.max(px, rx) && qx >= Math.min(px, rx) && qy <= Math.max(py, ry) && qy >= Math.min(py, ry)) {

    return true;
  }

  return false;
}

/*
 * To find orientation of ordered triplet (p, q, r). The function returns following values
 *
 *   0 --> p, q and r are colinear
 *   1 --> Clockwise
 *   2 --> Counterclockwise
 *
 */
function orientation(px: number, py: number, qx: number, qy: number, rx: number, ry: number): number {

  /* See https://www.geeksforgeeks.org/orientation-3-ordered-points/for details of below formula. */

  const val: number = (qy - py) * (rx - qx) - (qx - px) * (ry - qy);

  if (val == 0) {

    /* colinear */

    return 0;
  }

  /* clock or counterclock wise */

  return (val > 0)? 1: 2;
}

/**
 * returns true if line segment 'p1q1' and 'p2q2' intersect.
 *
 * [p1x,p1y], [q1x,q1y] - line #1
 * [p2x,p2y], [q2x,q2y] - line #2
 *
 */
export function linesIntersect(p1x: number, p1y: number, q1x: number, q1y: number, p2x: number, p2y: number, q2x: number, q2y: number): boolean {

  /* Find the four orientations needed for general and special cases */

  const o1: number = orientation(p1x, p1y, q1x, q1y, p2x, p2y);
  const o2: number = orientation(p1x, p1y, q1x, q1y, q2x, q2y);
  const o3: number = orientation(p2x, p2y, q2x, q2y, p1x, p1y);
  const o4: number = orientation(p2x, p2y, q2x, q2y, q1x, q1y);

  /* General case */

  if ((o1 != o2) && (o3 != o4)) {

    return true;
  }

  /* p1, q1 and p2 are colinear and p2 lies on segment p1q1 */

  if (o1 == 0 && onSegment(p1x, p1y, p2x, p2y, q1x, q1y)) return true;

  /* p1, q1 and q2 are colinear and q2 lies on segment p1q1 */

  if (o2 == 0 && onSegment(p1x, p1y, q2x, q2y, q1x, q1y)) return true;

  /* p2, q2 and p1 are colinear and p1 lies on segment p2q2 */

  if (o3 == 0 && onSegment(p2x, p2y, p1x, p1y, q2x, q2y)) return true;

  /* p2, q2 and q1 are colinear and q1 lies on segment p2q2 */

  if (o4 == 0 && onSegment(p2x, p2y, q1x, q1y, q2x, q2y)) return true;

  /* Doesn't fall in any of the above cases */

  return false;
}

/*
 * line and bounding box (axis aligned) intersection test
 *
 *   http://www.3dkingdoms.com/weekly/weekly.php?a=3
 *   https://stackoverflow.com/questions/3235385/given-a-bounding-box-and-a-line-two-points-determine-if-the-line-intersects-t
 *
 */

function inBox(hx: number, hy: number, b1x: number, b1y: number, b2x: number, b2y: number, axis: number) {

  if (axis == 1 && hy > b1y && hy < b2y) {

    return true;
  }

  if (axis == 2 && hx > b1x && hx < b2x) {

    return true;
  }

  if (axis == 3 && hx > b1x && hx < b2x && hy > b1y && hy < b2y) {

    return true;
  }

  return false;
}

function getIntersection(
  fDst1: number, fDst2: number,
  p1x: number,   p1y: number,
  p2x: number,   p2y: number,
  hit: {x: number, y: number}) {


  if ((fDst1 * fDst2) >= 0) {

    return false;
  }

  if (fDst1 == fDst2) {

    return false;
  }

  let temp: {x: number, y: number} = {x: p2x - p1x, y: p2y - p1y};

  temp.x *= (-fDst1 / (fDst2 - fDst1));
  temp.y *= (-fDst1 / (fDst2 - fDst1));

  hit.x = temp.x + p1x;
  hit.y = temp.y + p1y;

  return true;
}

/** @brief test if this axis aligned bounding box intersects with the given line */
export function intersectBox(
  b1x: number, b1y: number,   w: number,   h: number,
  l1x: number, l1y: number, l2x: number, l2y: number,
  hit: {x: number, y: number}) {

  const b2x = b1x + w;
  const b2y = b1y + h;

  if (l2x < b1x && l1x < b1x) {

    return false;
  }

  if (l2x > b2x && l1x > b2x) {

    return false;
  }

  if (l2y < b1y && l1y < b1y) {

    return false;
  }

  if (l2y > b2y && l1y > b2y) {

    return false;
  }

  if (l1x > b1x && l1x < b2x && l1y > b1y && l1y < b2y) {

    hit.x = l1x;
    hit.y = l1y;

    return true;
  }

  if ((getIntersection(l1x - b1x, l2x - b1x, l1x, l1y, l2x, l2y, hit) && inBox(hit.x, hit.y, b1x, b1y, b2x, b2y, 1)) ||
    (getIntersection(l1y - b1y, l2y - b1y, l1x, l1y, l2x, l2y, hit) && inBox(hit.x, hit.y, b1x, b1y, b2x, b2y, 2)) ||
    (getIntersection(l1x - b2x, l2x - b2x, l1x, l1y, l2x, l2y, hit) && inBox(hit.x, hit.y, b1x, b1y, b2x, b2y, 1)) ||
    (getIntersection(l1y - b2y, l2y - b2y, l1x, l1y, l2x, l2y, hit) && inBox(hit.x, hit.y, b1x, b1y, b2x, b2y, 2))) {

    return true;
  }

  return false;
}

/** fetch the distance between two points */
export function pointDistance(ax: number, ay: number, bx: number, by: number): number {

  let a:number = ax - bx;
  let b:number = ay - by;

  return Math.sqrt( a*a + b*b );
}
